import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { naytiveAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import './VisaManagement.css';

// Registered company's compliance calendar (materialised on "Complete"). Shows
// the per-entity filing schedule + a regenerate action, so filings/docs/actions
// all live in one place on the proposal.
function ProposalFilingsPanel({ entityId, entityName }) {
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const r = await naytiveAPI.listEntityFilings(entityId);
      setFilings((r.data && r.data.items) || []);
    } catch (e) {
      console.error('[filings load]', e);
    } finally {
      setLoading(false);
    }
  }, [entityId]);
  useEffect(() => { load(); }, [load]);

  const regenerate = async () => {
    setBusy(true); setMsg(null);
    try {
      await naytiveAPI.regenerateEntityFilings(entityId);
      await load();
      setMsg({ type: 'ok', text: 'Calendar regenerated' });
    } catch (e) {
      setMsg({ type: 'err', text: 'Regenerate failed' });
    } finally {
      setBusy(false);
    }
  };

  const STATUS_COLOR = {
    upcoming: '#6B7280', urgent: '#D97706', in_progress: '#2563EB',
    filed: '#059669', overdue: '#DC2626', waived: '#9CA3AF',
  };

  return (
    <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, padding: 14, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontWeight: 600, fontSize: 13 }}>
          Compliance calendar{entityName ? ` · ${entityName}` : ''}
        </div>
        <button type="button" className="btn-secondary btn-sm" onClick={regenerate} disabled={busy}>
          {busy ? 'Regenerating…' : 'Regenerate'}
        </button>
      </div>
      {msg && <div style={{ fontSize: 12, color: msg.type === 'ok' ? '#059669' : '#DC2626', marginBottom: 8 }}>{msg.text}</div>}
      {loading ? (
        <div style={{ fontSize: 12, color: '#6B7280' }}>Loading…</div>
      ) : filings.length === 0 ? (
        <div style={{ fontSize: 12, color: '#9CA3AF' }}>No filings yet — click Regenerate to build the calendar from the entity&apos;s profile.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#6B7280' }}>
              <th style={{ padding: '4px 6px' }}>Filing</th>
              <th style={{ padding: '4px 6px' }}>Due</th>
              <th style={{ padding: '4px 6px' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {filings.map((f) => (
              <tr key={f.id} style={{ borderTop: '1px solid #F3F4F6' }}>
                <td style={{ padding: '6px 6px' }}>
                  <div style={{ fontWeight: 500 }}>{f.template_name}</div>
                  {f.period_label && <div style={{ color: '#9CA3AF', fontSize: 11 }}>{f.period_label}</div>}
                </td>
                <td style={{ padding: '6px 6px', whiteSpace: 'nowrap' }}>{f.due_date ? String(f.due_date).slice(0, 10) : '—'}</td>
                <td style={{ padding: '6px 6px' }}>
                  <span style={{ color: STATUS_COLOR[f.status] || '#6B7280', fontWeight: 500 }}>{f.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Email the founder — type raw HTML or pick a saved template, preview, send.
// Templates are reusable and manageable (save / update / delete) right here.
function ProposalEmailPanel({ proposalId, userEmail }) {
  const [templates, setTemplates] = useState([]);
  const [selTpl, setSelTpl] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [mode, setMode] = useState('rich'); // 'rich' | 'source' | 'preview'
  const [attachments, setAttachments] = useState([]); // { filename, mime, base64, size }
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null);
  const editorRef = useRef(null);
  const fileRef = useRef(null);

  const loadTemplates = useCallback(async () => {
    try {
      const r = await naytiveAPI.listEmailTemplates();
      setTemplates((r.data && r.data.items) || []);
    } catch (e) {
      console.error('[email templates load]', e);
    }
  }, []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // Sync the WYSIWYG editor when bodyHtml changes externally (template load,
  // source-mode edit, switching back to rich) — but never while the user is
  // typing in it (that would jump the caret).
  useEffect(() => {
    const el = editorRef.current;
    if (!el || mode !== 'rich') return;
    if (document.activeElement !== el && el.innerHTML !== bodyHtml) {
      el.innerHTML = bodyHtml || '';
    }
  }, [bodyHtml, mode]);

  const onPickTemplate = (id) => {
    setSelTpl(id);
    const t = templates.find((x) => String(x.id) === String(id));
    if (t) { setSubject(t.subject); setBodyHtml(t.body_html); }
  };

  const flash = (type, text) => setMsg({ type, text });

  const exec = (cmd, arg) => {
    document.execCommand(cmd, false, arg);
    if (editorRef.current) setBodyHtml(editorRef.current.innerHTML);
  };
  const addLink = () => {
    const url = window.prompt('Link URL (https://…)');
    if (url) exec('createLink', url);
  };

  const onAttach = (e) => {
    const files = Array.from(e.target.files || []);
    Promise.all(files.map((f) => new Promise((res) => {
      const r = new FileReader();
      r.onload = () => res({ filename: f.name, mime: f.type || 'application/octet-stream', base64: String(r.result).split(',')[1] || '', size: f.size });
      r.readAsDataURL(f);
    }))).then((newOnes) => {
      setAttachments((prev) => {
        const next = [...prev, ...newOnes];
        if (next.reduce((s, a) => s + (a.size || 0), 0) > 10 * 1024 * 1024) {
          flash('err', 'Attachments exceed 10MB total');
          return prev;
        }
        return next;
      });
    });
    if (fileRef.current) fileRef.current.value = '';
  };
  const removeAttachment = (i) => setAttachments((prev) => prev.filter((_, idx) => idx !== i));

  const send = async () => {
    if (!subject.trim() || !bodyHtml.trim()) { flash('err', 'Subject and body are required'); return; }
    setSending(true); setMsg(null);
    try {
      const r = await naytiveAPI.sendEmail({
        proposalId, subject, bodyHtml, templateId: selTpl || undefined,
        attachments: attachments.map((a) => ({ filename: a.filename, mime: a.mime, base64: a.base64 })),
      });
      flash('ok', `Sent to ${(r.data && r.data.to) || userEmail || 'founder'}`);
    } catch (e) {
      flash('err', (e && e.response && e.response.data && e.response.data.message) || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const saveAsTemplate = async () => {
    const name = window.prompt('Template name?');
    if (!name) return;
    if (!subject.trim() || !bodyHtml.trim()) { flash('err', 'Add a subject and body first'); return; }
    try { await naytiveAPI.createEmailTemplate({ name, subject, bodyHtml }); await loadTemplates(); flash('ok', 'Template saved'); }
    catch (e) { flash('err', 'Could not save template'); }
  };

  const updateTemplate = async () => {
    if (!selTpl) return;
    try { await naytiveAPI.updateEmailTemplate(selTpl, { subject, bodyHtml }); await loadTemplates(); flash('ok', 'Template updated'); }
    catch (e) { flash('err', 'Could not update template'); }
  };

  const deleteTemplate = async () => {
    if (!selTpl || !window.confirm('Delete this template?')) return;
    try { await naytiveAPI.deleteEmailTemplate(selTpl); setSelTpl(''); await loadTemplates(); flash('ok', 'Template deleted'); }
    catch (e) { flash('err', 'Could not delete'); }
  };

  const TB = { padding: '4px 9px', border: '1px solid #D1D5DB', borderRadius: 5, background: '#fff', cursor: 'pointer', fontSize: 12.5, lineHeight: 1 };
  // onMouseDown preventDefault keeps the editor's selection while clicking a
  // toolbar button, so execCommand applies to the selected text.
  const tbtn = (label, cmd, arg) => (
    <button type="button" style={TB} onMouseDown={(e) => e.preventDefault()} onClick={() => exec(cmd, arg)}>{label}</button>
  );

  return (
    <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, padding: 14, marginBottom: 16 }}>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
        Email the founder{userEmail ? ` · ${userEmail}` : ''}
      </div>
      <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 10 }}>
        Format with the toolbar (or edit raw HTML), attach files, pick a template. Send now or at completion.
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
        <select value={selTpl} onChange={(e) => onPickTemplate(e.target.value)} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13 }}>
          <option value="">— Pick a template —</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        {selTpl && <button type="button" className="btn-secondary btn-sm" onClick={updateTemplate}>Update template</button>}
        {selTpl && <button type="button" className="btn-secondary btn-sm" onClick={deleteTemplate}>Delete</button>}
        <button type="button" className="btn-secondary btn-sm" onClick={saveAsTemplate}>Save as template</button>
      </div>

      <input
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        placeholder="Subject"
        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13, marginBottom: 8, boxSizing: 'border-box' }}
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
        {['rich', 'source', 'preview'].map((m) => (
          <button key={m} type="button" className="btn-secondary btn-sm" onClick={() => setMode(m)} style={{ opacity: mode === m ? 1 : 0.55, textTransform: 'capitalize' }}>
            {m === 'source' ? 'HTML' : m}
          </button>
        ))}
      </div>

      {mode === 'rich' && (
        <>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
            {tbtn(<b>B</b>, 'bold')}
            {tbtn(<i>I</i>, 'italic')}
            {tbtn(<u>U</u>, 'underline')}
            {tbtn('H2', 'formatBlock', 'H2')}
            {tbtn('¶', 'formatBlock', 'P')}
            {tbtn('• List', 'insertUnorderedList')}
            {tbtn('1. List', 'insertOrderedList')}
            <button type="button" style={TB} onMouseDown={(e) => e.preventDefault()} onClick={addLink}>Link</button>
            {tbtn('Clear', 'removeFormat')}
          </div>
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            onInput={() => editorRef.current && setBodyHtml(editorRef.current.innerHTML)}
            style={{ minHeight: 180, maxHeight: 340, overflowY: 'auto', padding: 12, borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13, background: '#fff', lineHeight: 1.5 }}
          />
        </>
      )}
      {mode === 'source' && (
        <textarea
          value={bodyHtml}
          onChange={(e) => setBodyHtml(e.target.value)}
          placeholder={'<p>Hi there,</p>\n<p>Your company is now registered …</p>'}
          style={{ width: '100%', minHeight: 200, padding: 10, borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13, fontFamily: 'monospace', boxSizing: 'border-box' }}
        />
      )}
      {mode === 'preview' && (
        <iframe title="email-preview" srcDoc={bodyHtml} style={{ width: '100%', minHeight: 200, border: '1px solid #E5E7EB', borderRadius: 6, background: '#fff' }} />
      )}

      <div style={{ marginTop: 10 }}>
        <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>Attachments</div>
        <input ref={fileRef} type="file" multiple onChange={onAttach} style={{ fontSize: 13 }} />
        {attachments.length > 0 && (
          <ul style={{ margin: '6px 0 0', paddingLeft: 16, fontSize: 12.5 }}>
            {attachments.map((a, i) => (
              <li key={`${a.filename}-${i}`} style={{ marginBottom: 2 }}>
                {a.filename} <span style={{ color: '#9CA3AF' }}>({Math.round((a.size || 0) / 1024)} KB)</span>{' '}
                <button type="button" onClick={() => removeAttachment(i)} style={{ border: 'none', background: 'none', color: '#DC2626', cursor: 'pointer', fontSize: 12 }}>remove</button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {msg && <div style={{ marginTop: 8, fontSize: 12, color: msg.type === 'ok' ? '#059669' : '#DC2626' }}>{msg.text}</div>}
      <div style={{ marginTop: 10 }}>
        <button type="button" className="btn-primary btn-sm" onClick={send} disabled={sending} style={{ background: '#2563EB' }}>
          {sending ? 'Sending…' : 'Send email'}
        </button>
      </div>
    </div>
  );
}

// Team document uploads for a proposal — certificate of incorporation + any
// other entity docs. Written to the founder's vault so they appear on the
// founder's Company page.
function ProposalDocumentsPanel({ proposalId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState('CERTIFICATE_OF_INCORPORATION');
  const [err, setErr] = useState(null);
  const [packing, setPacking] = useState(false);
  const fileRef = useRef(null);

  const blobDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename || 'download';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const downloadDoc = async (d) => {
    try {
      const res = await naytiveAPI.downloadUserDocument(d.id);
      blobDownload(res.data, d.file_name || `document-${d.id}`);
    } catch (e) { setErr('Could not download document'); }
  };
  const downloadPacket = async () => {
    setPacking(true); setErr(null);
    try {
      const res = await naytiveAPI.downloadProposalPacket(proposalId);
      blobDownload(res.data, 'proposal-packet.zip');
    } catch (e) { setErr('Could not download packet'); }
    finally { setPacking(false); }
  };

  const load = useCallback(async () => {
    if (!proposalId) return;
    setLoading(true);
    try {
      const res = await naytiveAPI.listProposalDocuments(proposalId);
      setDocs(res.data?.items || []);
    } catch (e) {
      console.error('[ProposalDocumentsPanel] load failed', e);
    } finally {
      setLoading(false);
    }
  }, [proposalId]);

  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setErr(null);
    if (file.size > 10 * 1024 * 1024) {
      setErr('File too large (max 10MB)');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result).split(',')[1] || '');
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      await naytiveAPI.uploadProposalDocument(proposalId, {
        fileName: file.name,
        fileMime: file.type || 'application/octet-stream',
        fileBase64: b64,
        docType,
      });
      if (fileRef.current) fileRef.current.value = '';
      await load();
    } catch (e2) {
      console.error('[ProposalDocumentsPanel] upload failed', e2);
      setErr((e2 && e2.response && e2.response.data && e2.response.data.message) || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const TYPES = [
    ['CERTIFICATE_OF_INCORPORATION', 'Certificate of Incorporation'],
    ['MEMORANDUM_OF_ASSOCIATION', 'Memorandum of Association'],
    ['ARTICLES_OF_ASSOCIATION', 'Articles of Association'],
    ['OPERATING_AGREEMENT', 'Operating Agreement'],
    ['SHARE_CERTIFICATE', 'Share Certificate'],
    ['TAX_REGISTRATION', 'Tax Registration'],
    ['BOARD_RESOLUTION', 'Board Resolution'],
    ['POWER_OF_ATTORNEY', 'Power of Attorney'],
    ['OTHER_DOC', 'Other document'],
  ];

  return (
    <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, padding: 14, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, gap: 10 }}>
        <div style={{ fontWeight: 600, fontSize: 13 }}>Company documents</div>
        <button type="button" className="btn-secondary btn-sm" onClick={downloadPacket} disabled={packing}>
          {packing ? 'Preparing…' : 'Download packet (.zip)'}
        </button>
      </div>
      <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 10 }}>
        Upload the certificate of incorporation and any other entity documents (they appear on the founder&apos;s Company page). The packet zips every uploaded document + all collected proposal data.
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          value={docType}
          onChange={(e) => setDocType(e.target.value)}
          style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13 }}
        >
          {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input ref={fileRef} type="file" onChange={onFile} disabled={uploading} style={{ fontSize: 13 }} />
        {uploading && <span style={{ fontSize: 12, color: '#6B7280' }}>Uploading…</span>}
      </div>
      {err && <div style={{ color: '#DC2626', fontSize: 12, marginTop: 6 }}>{err}</div>}
      <div style={{ marginTop: 10 }}>
        {loading ? (
          <div style={{ fontSize: 12, color: '#6B7280' }}>Loading…</div>
        ) : docs.length === 0 ? (
          <div style={{ fontSize: 12, color: '#9CA3AF' }}>No documents uploaded yet.</div>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 16, fontSize: 13 }}>
            {docs.map((d) => (
              <li key={d.id} style={{ marginBottom: 3 }}>
                <span style={{ fontWeight: 500 }}>{d.file_name}</span>
                <span style={{ color: '#6B7280' }}> · {d.doc_type_name || d.doc_type}</span>
                <span style={{ color: d.source === 'admin' ? '#2563EB' : '#059669', fontSize: 11, marginLeft: 6 }}>
                  {d.source === 'admin' ? 'team' : 'founder'}
                </span>
                <button type="button" onClick={() => downloadDoc(d)} style={{ border: 'none', background: 'none', color: '#2563EB', cursor: 'pointer', fontSize: 12, marginLeft: 8, padding: 0 }}>download</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


/**
 * Incorporation Proposals queue.
 *
 * Visual chrome mirrors VisaManagement: header with title + status
 * dropdown + REFRESH, stat cards for ALL / TENTATIVE / FINAL, single
 * data-table, and a per-row modal (opens via OPEN button) for full
 * review + edit. The modal hosts the case-specific pricing breakdown
 * editor and the "Publish Final" CTA — flipping `pricing_kind` to
 * `final` is what re-enables Accept on the user dashboard drawer.
 */

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'declined', label: 'Declined' },
];
const STATUS_BADGES = {
  draft:    { label: 'Draft',    bg: '#F3F4F6', fg: '#6B7280' },
  sent:     { label: 'Sent',     bg: '#DBEAFE', fg: '#1E40AF' },
  accepted: { label: 'Accepted', bg: '#DCFCE7', fg: '#166534' },
  declined: { label: 'Declined', bg: '#FEE2E2', fg: '#B91C1C' },
};
const PRICING_BADGES = {
  tentative: { label: 'Tentative', bg: '#FEF3C7', fg: '#92400E' },
  final:     { label: 'Final',     bg: '#DCFCE7', fg: '#166534' },
};

const fmtMoney = (cents, currency = 'USD') => {
  if (cents === null || cents === undefined) return '—';
  const major = Number(cents) / 100;
  const symbol =
    currency === 'USD' ? '$' :
    currency === 'EUR' ? '€' :
    currency === 'GBP' ? '£' :
    currency === 'INR' ? '₹' : '';
  return symbol
    ? `${symbol}${major.toLocaleString('en-US', { maximumFractionDigits: 2 })}`
    : `${major.toLocaleString('en-US')} ${currency}`;
};
const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const Badge = ({ map, value }) => {
  const b = map[value] || { label: value, bg: '#F3F4F6', fg: '#6B7280' };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 10px',
        borderRadius: 999,
        background: b.bg,
        color: b.fg,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
      }}
    >
      {b.label}
    </span>
  );
};

const NaytiveProposals = () => {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [pricingFilter, setPricingFilter] = useState('');
  const [toast, setToast] = useState(null);
  const [modal, setModal] = useState({ open: false, id: null });
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef(null);

  const fetchProposals = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (pricingFilter) params.pricingKind = pricingFilter;
      const res = await naytiveAPI.listIncorporationProposals(params);
      setProposals(res.data?.items || []);
    } catch (err) {
      console.error('[NaytiveProposals] fetch failed', err);
      setToast({ type: 'error', text: 'Failed to load proposals' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, pricingFilter]);

  useEffect(() => { fetchProposals(); }, [fetchProposals]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!statusDropdownOpen) return;
    const onDoc = (e) => {
      if (statusDropdownRef.current?.contains(e.target)) return;
      setStatusDropdownOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [statusDropdownOpen]);

  const counts = useMemo(() => {
    const c = { all: proposals.length, tentative: 0, final: 0 };
    for (const p of proposals) c[p.pricing_kind] = (c[p.pricing_kind] || 0) + 1;
    return c;
  }, [proposals]);

  const openProposal = (id) => setModal({ open: true, id });
  const closeModal = () => setModal({ open: false, id: null });

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">PROPOSALS</h1>
            <p className="page-subtitle">
              AUTO-CREATED ON BOOKING · PUBLISH FINAL TO ENABLE ACCEPT
            </p>
          </div>
          <div className="header-right">
            <div className="custom-dropdown" ref={statusDropdownRef}>
              <button
                className="custom-dropdown-trigger"
                onClick={() => setStatusDropdownOpen((o) => !o)}
              >
                <span>
                  {STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label}
                </span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {statusDropdownOpen && (
                <div className="custom-dropdown-menu">
                  {STATUS_OPTIONS.map((opt) => (
                    <div
                      key={opt.value || 'all'}
                      className={`custom-dropdown-item ${
                        statusFilter === opt.value ? 'active' : ''
                      }`}
                      onClick={() => {
                        setStatusFilter(opt.value);
                        setStatusDropdownOpen(false);
                      }}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              className="btn-primary"
              onClick={fetchProposals}
              disabled={loading}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path>
                <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"></path>
              </svg>
              REFRESH
            </button>
          </div>
        </div>

        <div className="dashboard-content">
          <div
            className="stats-grid"
            style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}
          >
            <div
              className={`stat-card stat-card-clickable ${
                pricingFilter === '' ? 'stat-card-active' : ''
              }`}
              onClick={() => setPricingFilter('')}
            >
              <div className="stat-info">
                <span className="stat-label">ALL</span>
                <span className="stat-value">{counts.all}</span>
              </div>
            </div>
            <div
              className={`stat-card stat-card-clickable ${
                pricingFilter === 'tentative' ? 'stat-card-active' : ''
              }`}
              onClick={() => setPricingFilter('tentative')}
            >
              <div className="stat-info">
                <span className="stat-label">TENTATIVE</span>
                <span className="stat-value">{counts.tentative || 0}</span>
              </div>
            </div>
            <div
              className={`stat-card stat-success stat-card-clickable ${
                pricingFilter === 'final' ? 'stat-card-active' : ''
              }`}
              onClick={() => setPricingFilter('final')}
            >
              <div className="stat-info">
                <span className="stat-label">FINAL</span>
                <span className="stat-value">{counts.final || 0}</span>
              </div>
            </div>
          </div>

          <div className="dashboard-section">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading proposals…</p>
              </div>
            ) : proposals.length === 0 ? (
              <div className="empty-state">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                </svg>
                <p>No proposals match the current filters.</p>
              </div>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>USER</th>
                      <th>JURISDICTION</th>
                      <th>TITLE</th>
                      <th>TOTAL</th>
                      <th>PRICING</th>
                      <th>STATUS</th>
                      <th>CREATED</th>
                      <th>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {proposals.map((p) => (
                      <tr key={p.id}>
                        <td>
                          <div className="destination-info">
                            <div className="destination-details">
                              <span className="destination-name">
                                {[p.first_name, p.last_name].filter(Boolean).join(' ') || '—'}
                              </span>
                              <span className="destination-category">
                                {p.user_email || '—'}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="visa-type-info">
                            <span className="visa-name">
                              {p.jurisdiction_name || p.jurisdiction_slug}
                            </span>
                            <span style={{ fontSize: 11, color: '#9CA3AF' }}>
                              {p.jurisdiction_slug} · {p.country_code}
                            </span>
                          </div>
                        </td>
                        <td style={{ maxWidth: 240 }}>
                          <div
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {/* Once registered, the single finalised company name
                                is the truth; before that, show the proposal title
                                (which for self-serve is the applied name choices). */}
                            {p.registered_name || p.title}
                          </div>
                          {p.registered_name && p.entity_legal_name &&
                            p.entity_legal_name !== p.registered_name && (
                              <div style={{ fontSize: 11, color: '#9CA3AF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                applied: {p.entity_legal_name}
                              </div>
                            )}
                        </td>
                        <td style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                          {fmtMoney(p.total_cost_cents, p.currency)}
                        </td>
                        <td><Badge map={PRICING_BADGES} value={p.pricing_kind} /></td>
                        <td><Badge map={STATUS_BADGES} value={p.status} /></td>
                        <td style={{ fontSize: 12, color: '#6B7280' }}>
                          {fmtDate(p.created_at)}
                        </td>
                        <td>
                          <button
                            className="btn-action btn-edit"
                            onClick={() => openProposal(p.id)}
                            title="Open"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M5 12h14"></path>
                              <path d="M12 5l7 7-7 7"></path>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {modal.open && (
          <ProposalModal
            id={modal.id}
            onClose={closeModal}
            onSaved={async () => {
              await fetchProposals();
              setToast({ type: 'success', text: 'Saved' });
            }}
          />
        )}

        {toast && (
          <div className={`naytive-toast ${toast.type === 'error' ? 'toast-error' : 'toast-success'}`}>
            {toast.text}
          </div>
        )}
      </main>
    </div>
  );
};

/* ──────────────────── Detail / Edit Modal ──────────────────── */

export function ProposalModal({ id, onClose, onSaved }) {
  const [item, setItem] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [stageEvents, setStageEvents] = useState([]);
  const [stageBusy, setStageBusy] = useState(false);
  const [docTypes, setDocTypes] = useState([]);
  const [actions, setActions] = useState([]);
  const [actionBusy, setActionBusy] = useState(false);
  const [newAction, setNewAction] = useState({ title: '', assigned_to: 'user' });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await naytiveAPI.getIncorporationProposal(id);
      const it = res.data?.item || null;
      setItem(it);
      if (it) {
        const pricing = parseJson(it.pricing_json, []);
        const timeline = parseJson(it.timeline_json, []).map((r, i) => ({
          week: r.week ?? r.weeks ?? i + 1,
          title: r.title || '',
          description: r.description || '',
        }));
        const requirements = parseJson(it.requirements_json, []).map((r) => ({
          label: r.label || '',
          subtitle: r.subtitle || '',
          documentTypeCode: r.documentTypeCode || r.document_type_code || '',
          optional: r.optional === true,
        }));
        setDraft({
          title: it.title || '',
          description: it.description || '',
          timeline_weeks: it.timeline_weeks ?? '',
          travel_required: it.travel_required || 'None',
          entity_structure: it.entity_structure || '',
          currency: it.currency || 'USD',
          pricing,
          timeline,
          requirements,
          total_cost_cents: pricing.length
            ? pricing.reduce((s, r) => s + Number(r.amountCents || 0), 0)
            : Number(it.total_cost_cents || 0),
        });
      }
    } catch (err) {
      console.error('[ProposalModal] load failed', err);
      setError('Failed to load proposal');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadStageEvents = useCallback(async () => {
    try {
      const res = await naytiveAPI.listStageEvents(id);
      setStageEvents(res.data?.items || []);
    } catch (err) {
      console.error('[ProposalModal] stage events load failed', err);
    }
  }, [id]);

  const loadActions = useCallback(async () => {
    try {
      const r = await naytiveAPI.listProposalActions(id);
      setActions(r.data?.items || []);
    } catch (err) {
      console.error('[ProposalModal] actions load failed', err);
    }
  }, [id]);

  useEffect(() => {
    load();
    loadStageEvents();
    loadActions();
  }, [load, loadStageEvents, loadActions]);

  // Cached doc-type catalog for the requirements editor picker.
  useEffect(() => {
    let cancelled = false;
    naytiveAPI.listDocumentTypes()
      .then((r) => { if (!cancelled) setDocTypes(r.data?.items || []); })
      .catch(() => { if (!cancelled) setDocTypes([]); });
    return () => { cancelled = true; };
  }, []);

  const stages = useMemo(() => parseJson(item?.timeline_json, []), [item]);
  const eventsByStage = useMemo(() => {
    const m = {};
    for (const e of stageEvents) {
      (m[e.stage_index] ||= []).push(e);
    }
    return m;
  }, [stageEvents]);

  const addStageEvent = async (stageIndex, body) => {
    setStageBusy(true);
    try {
      await naytiveAPI.addStageEvent(id, { stage_index: stageIndex, ...body });
      await Promise.all([loadStageEvents(), load()]);
    } catch (err) {
      console.error('[ProposalModal] addStageEvent failed', err);
      setError(err.response?.data?.message || 'Failed to log event');
    } finally {
      setStageBusy(false);
    }
  };
  const deleteStageEvent = async (eventId) => {
    setStageBusy(true);
    try {
      await naytiveAPI.deleteStageEvent(id, eventId);
      await loadStageEvents();
    } catch (err) {
      console.error('[ProposalModal] deleteStageEvent failed', err);
    } finally {
      setStageBusy(false);
    }
  };

  /* ── Action items ───────────────────────────────────────────── */
  const addAction = async () => {
    const title = newAction.title.trim();
    if (!title) return;
    setActionBusy(true);
    try {
      await naytiveAPI.addProposalAction(id, {
        title,
        assigned_to: newAction.assigned_to,
        status: 'todo',
        display_order: actions.length + 1,
        visible_to_user: true,
      });
      setNewAction({ title: '', assigned_to: 'user' });
      await loadActions();
    } catch (err) {
      console.error('[ProposalModal] addAction failed', err);
    } finally {
      setActionBusy(false);
    }
  };
  const patchAction = async (actionId, patch) => {
    setActionBusy(true);
    try {
      await naytiveAPI.updateProposalAction(id, actionId, patch);
      await loadActions();
    } catch (err) {
      console.error('[ProposalModal] patchAction failed', err);
    } finally {
      setActionBusy(false);
    }
  };
  const deleteAction = async (actionId) => {
    if (!window.confirm('Delete this action item?')) return;
    setActionBusy(true);
    try {
      await naytiveAPI.deleteProposalAction(id, actionId);
      await loadActions();
    } catch (err) {
      console.error('[ProposalModal] deleteAction failed', err);
    } finally {
      setActionBusy(false);
    }
  };

  const updateRow = (idx, patch) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = [...prev.pricing];
      next[idx] = { ...next[idx], ...patch };
      const total = next.reduce((s, r) => s + Number(r.amountCents || 0), 0);
      return { ...prev, pricing: next, total_cost_cents: total };
    });
  };
  const addRow = () => {
    setDraft((prev) =>
      prev
        ? { ...prev, pricing: [...prev.pricing, { label: '', amountCents: 0 }] }
        : prev,
    );
  };
  const removeRowAt = (idx) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = prev.pricing.filter((_, i) => i !== idx);
      const total = next.reduce((s, r) => s + Number(r.amountCents || 0), 0);
      return { ...prev, pricing: next, total_cost_cents: total };
    });
  };

  /* ── Timeline editor (full rewrite per-case) ─────────────────── */
  const updateStage = (idx, patch) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = [...prev.timeline];
      next[idx] = { ...next[idx], ...patch };
      return { ...prev, timeline: next };
    });
  };
  const addStage = () => {
    setDraft((prev) => {
      if (!prev) return prev;
      const lastWeek = prev.timeline.reduce(
        (m, r) => Math.max(m, Number(r.week) || 0),
        0,
      );
      return {
        ...prev,
        timeline: [
          ...prev.timeline,
          { week: lastWeek + 1, title: '', description: '' },
        ],
      };
    });
  };
  const removeStageAt = (idx) => {
    if (!window.confirm(
      'Remove this stage from the timeline?\n\n' +
      'Any logged events for this stage will be left behind ' +
      'and shown as orphaned. Continue?',
    )) return;
    setDraft((prev) => {
      if (!prev) return prev;
      return { ...prev, timeline: prev.timeline.filter((_, i) => i !== idx) };
    });
  };
  /* ── Requirements (per-case override) ────────────────────────── */
  const updateReq = (idx, patch) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = [...prev.requirements];
      next[idx] = { ...next[idx], ...patch };
      return { ...prev, requirements: next };
    });
  };
  const addReq = () => {
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            requirements: [
              ...prev.requirements,
              { label: '', subtitle: '', documentTypeCode: '', optional: false },
            ],
          }
        : prev,
    );
  };
  const removeReqAt = (idx) => {
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        requirements: prev.requirements.filter((_, i) => i !== idx),
      };
    });
  };

  const moveStage = (idx, dir) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = [...prev.timeline];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return { ...prev, timeline: next };
    });
  };

  const save = async (publishFinal) => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      const patch = {
        title: draft.title,
        description: draft.description,
        timeline_weeks: draft.timeline_weeks === '' ? null : Number(draft.timeline_weeks),
        travel_required: draft.travel_required,
        entity_structure: draft.entity_structure,
        currency: draft.currency,
        pricing_json: draft.pricing.map((r) => ({
          label: r.label,
          amountCents: Math.round(Number(r.amountCents || 0)),
        })),
        timeline_json: draft.timeline
          .filter((r) => r.title.trim() || r.description.trim())
          .map((r) => ({
            week: Number(r.week) || 1,
            title: r.title || '',
            description: r.description || '',
          })),
        requirements_json: draft.requirements
          .filter((r) => r.label.trim() || r.documentTypeCode)
          .map((r) => ({
            label: r.label || '',
            subtitle: r.subtitle || '',
            documentTypeCode: r.documentTypeCode || undefined,
            optional: !!r.optional,
          })),
        total_cost_cents: Math.round(Number(draft.total_cost_cents || 0)),
      };
      if (publishFinal) patch.publish_final = true;
      await naytiveAPI.updateIncorporationProposal(id, patch);
      await onSaved();
      await load();
    } catch (err) {
      console.error('[ProposalModal] save failed', err);
      setError(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content modal-form"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 860 }}
      >
        <div className="modal-header">
          <h2 className="modal-title">
            {loading ? 'Loading…' : `Proposal #${id} · ${item?.registered_name || item?.title || ''}`}
          </h2>
        </div>

        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {loading || !draft ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading proposal…</p>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <Badge map={PRICING_BADGES} value={item.pricing_kind} />
                <Badge map={STATUS_BADGES} value={item.status} />
              </div>

              <EntityCompletionPanel item={item} onCompleted={load} />

              {item.entity_id && (
                <ProposalFilingsPanel entityId={item.entity_id} entityName={item.entity && item.entity.name} />
              )}

              <ProposalDocumentsPanel proposalId={id} />

              <ProposalEmailPanel proposalId={id} userEmail={item.user_email} />

              <div className="form-row">
                <div className="form-group">
                  <label>User</label>
                  <input
                    type="text"
                    readOnly
                    value={
                      `${[item.first_name, item.last_name].filter(Boolean).join(' ') || '—'} · ${
                        item.user_email || ''
                      }`
                    }
                    style={{ background: '#F9FAFB' }}
                  />
                </div>
                <div className="form-group">
                  <label>Jurisdiction</label>
                  <input
                    type="text"
                    readOnly
                    value={`${item.jurisdiction_name || item.jurisdiction_slug} · ${item.country_code}${
                      item.booking_id ? ` · Booking #${item.booking_id}` : ''
                    }`}
                    style={{ background: '#F9FAFB' }}
                  />
                </div>
              </div>

              <div className="form-group full-width">
                <label>Title *</label>
                <input
                  type="text"
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                />
              </div>

              <div className="form-group full-width">
                <label>Description</label>
                <textarea
                  rows={3}
                  value={draft.description}
                  onChange={(e) =>
                    setDraft({ ...draft, description: e.target.value })
                  }
                />
              </div>

              <div className="form-row-3">
                <div className="form-group">
                  <label>Timeline (weeks)</label>
                  <input
                    type="number"
                    min="0"
                    value={draft.timeline_weeks}
                    onChange={(e) =>
                      setDraft({ ...draft, timeline_weeks: e.target.value })
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Travel</label>
                  <input
                    type="text"
                    value={draft.travel_required}
                    onChange={(e) =>
                      setDraft({ ...draft, travel_required: e.target.value })
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Structure</label>
                  <input
                    type="text"
                    value={draft.entity_structure}
                    onChange={(e) =>
                      setDraft({ ...draft, entity_structure: e.target.value })
                    }
                  />
                </div>
              </div>

              <div
                style={{
                  marginTop: 18,
                  marginBottom: 10,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: '#9CA3AF',
                  textTransform: 'uppercase',
                }}
              >
                Pricing breakdown ({draft.pricing.length} rows)
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {draft.pricing.map((r, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 160px 36px',
                      gap: 8,
                      alignItems: 'center',
                    }}
                  >
                    <input
                      type="text"
                      placeholder="Label"
                      value={r.label}
                      onChange={(e) => updateRow(i, { label: e.target.value })}
                    />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={(r.amountCents || 0) / 100}
                      onChange={(e) =>
                        updateRow(i, {
                          amountCents: Math.round(Number(e.target.value) * 100),
                        })
                      }
                    />
                    <button
                      type="button"
                      className="btn-action btn-delete"
                      onClick={() => removeRowAt(i)}
                      title="Remove row"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={addRow}
                  style={{ alignSelf: 'flex-start', marginTop: 4 }}
                >
                  + ADD ROW
                </button>
              </div>

              <div
                style={{
                  marginTop: 14,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 14px',
                  background: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: 8,
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 600 }}>Total</span>
                <span
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {fmtMoney(draft.total_cost_cents, draft.currency)}
                </span>
              </div>

              <div
                style={{
                  marginTop: 22,
                  marginBottom: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    color: '#9CA3AF',
                    textTransform: 'uppercase',
                  }}
                >
                  Required documents ({draft.requirements.length}) · per-case override
                </span>
                <span style={{ fontSize: 11, color: '#9CA3AF' }}>
                  Loaded from jurisdiction defaults — rewrite as needed
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {draft.requirements.map((r, i) => (
                  <div
                    key={i}
                    style={{
                      border: '1px solid #E5E7EB',
                      borderRadius: 8,
                      padding: 10,
                      background: '#FFFFFF',
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 60px 32px',
                      gap: 8,
                      alignItems: 'flex-start',
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <input
                        type="text"
                        placeholder="Label (e.g. Passport copy)"
                        value={r.label}
                        onChange={(e) => updateReq(i, { label: e.target.value })}
                      />
                      <input
                        type="text"
                        placeholder="Subtitle (e.g. For Mexican notary)"
                        value={r.subtitle}
                        onChange={(e) => updateReq(i, { subtitle: e.target.value })}
                      />
                    </div>
                    <select
                      value={r.documentTypeCode}
                      onChange={(e) =>
                        updateReq(i, { documentTypeCode: e.target.value })
                      }
                    >
                      <option value="">(no doc-type link)</option>
                      {docTypes.map((t) => (
                        <option key={t.id} value={t.code}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                    <label
                      style={{
                        fontSize: 11,
                        color: '#6B7280',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!!r.optional}
                        onChange={(e) => updateReq(i, { optional: e.target.checked })}
                      />
                      Opt
                    </label>
                    <button
                      type="button"
                      className="btn-action btn-delete"
                      onClick={() => removeReqAt(i)}
                      title="Remove"
                      style={{ width: 28, height: 28 }}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={addReq}
                  style={{ alignSelf: 'flex-start', marginTop: 4 }}
                >
                  + ADD REQUIREMENT
                </button>
              </div>

              <div
                style={{
                  marginTop: 22,
                  marginBottom: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    color: '#9CA3AF',
                    textTransform: 'uppercase',
                  }}
                >
                  Timeline stages ({draft.timeline.length}) · editable per-case
                </span>
                <span style={{ fontSize: 11, color: '#9CA3AF' }}>
                  Loaded from jurisdiction template — rewrite as needed
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {draft.timeline.map((r, i) => (
                  <div
                    key={i}
                    style={{
                      border: '1px solid #E5E7EB',
                      borderRadius: 8,
                      padding: 10,
                      background: '#FFFFFF',
                      display: 'grid',
                      gridTemplateColumns: '70px 1fr auto',
                      gap: 8,
                      alignItems: 'flex-start',
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <label style={{ fontSize: 10, color: '#9CA3AF' }}>
                        WEEK
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={r.week}
                        onChange={(e) =>
                          updateStage(i, { week: e.target.value })
                        }
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <input
                        type="text"
                        placeholder="Stage title (e.g. Kickoff & Power of Attorney)"
                        value={r.title}
                        onChange={(e) =>
                          updateStage(i, { title: e.target.value })
                        }
                      />
                      <textarea
                        rows={2}
                        placeholder="Description shown to the user"
                        value={r.description}
                        onChange={(e) =>
                          updateStage(i, { description: e.target.value })
                        }
                      />
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        alignItems: 'center',
                      }}
                    >
                      <button
                        type="button"
                        className="btn-action btn-edit"
                        title="Move up"
                        disabled={i === 0}
                        onClick={() => moveStage(i, -1)}
                        style={{ width: 28, height: 22 }}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="btn-action btn-edit"
                        title="Move down"
                        disabled={i === draft.timeline.length - 1}
                        onClick={() => moveStage(i, +1)}
                        style={{ width: 28, height: 22 }}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="btn-action btn-delete"
                        title="Remove stage"
                        onClick={() => removeStageAt(i)}
                        style={{ width: 28, height: 22 }}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={addStage}
                  style={{ alignSelf: 'flex-start', marginTop: 4 }}
                >
                  + ADD STAGE
                </button>
              </div>

              <StageTracker
                stages={stages}
                currentStageIndex={item?.current_stage_index}
                eventsByStage={eventsByStage}
                busy={stageBusy}
                onAdd={addStageEvent}
                onDelete={deleteStageEvent}
              />

              <ActionItemsEditor
                stages={stages}
                actions={actions}
                busy={actionBusy}
                newAction={newAction}
                setNewAction={setNewAction}
                onAdd={addAction}
                onPatch={patchAction}
                onDelete={deleteAction}
              />
            </>
          )}
        </div>

        {error && (
          <div className="modal-error">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
            {error}
          </div>
        )}

        <div className="modal-footer">
          <button
            type="button"
            className="btn-cancel"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-save"
            disabled={saving || loading}
            onClick={() => save(false)}
            style={{ background: '#6B7280' }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            className="btn-save"
            disabled={saving || loading || item?.pricing_kind === 'final'}
            onClick={() => save(true)}
            title={
              item?.pricing_kind === 'final'
                ? 'Already final'
                : 'Save + flip pricing_kind to final (enables Accept on the user drawer)'
            }
          >
            {saving ? 'Publishing…' : 'Publish Final'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────── Stage Tracker (Option C) ──────────────────── */

const STAGE_STATUS_BADGES = {
  pending: { label: 'Pending', bg: '#F3F4F6', fg: '#6B7280' },
  active:  { label: 'Active',  bg: '#FEF3C7', fg: '#92400E' },
  done:    { label: 'Done',    bg: '#DCFCE7', fg: '#166534' },
};

function deriveStageStatus(stageIdx, events, currentStageIndex) {
  const list = events || [];
  const started = list.some((e) => e.event_type === 'started');
  const completed = list.some((e) => e.event_type === 'completed');
  if (completed) return 'done';
  if (started || currentStageIndex === stageIdx) return 'active';
  return 'pending';
}

function StageTracker({
  stages, currentStageIndex, eventsByStage, busy, onAdd, onDelete,
}) {
  if (!Array.isArray(stages) || stages.length === 0) {
    return (
      <div
        style={{
          marginTop: 18,
          padding: 14,
          background: '#F9FAFB',
          border: '1px dashed #E5E7EB',
          borderRadius: 8,
          fontSize: 12,
          color: '#6B7280',
        }}
      >
        No timeline stages defined on this proposal yet.
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          marginTop: 22,
          marginBottom: 10,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.5,
          color: '#9CA3AF',
          textTransform: 'uppercase',
        }}
      >
        Stage tracker · log events ({stages.length} saved stages)
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {stages.map((stage, idx) => {
          const events = eventsByStage[idx] || [];
          const status = deriveStageStatus(idx, events, currentStageIndex);
          return (
            <StageRow
              key={idx}
              index={idx}
              stage={stage}
              status={status}
              events={events}
              busy={busy}
              onAdd={onAdd}
              onDelete={onDelete}
            />
          );
        })}
      </div>
    </>
  );
}

function StageRow({ index, stage, status, events, busy, onAdd, onDelete }) {
  const [expanded, setExpanded] = useState(status === 'active');
  const [noteDraft, setNoteDraft] = useState('');
  const [noteVisible, setNoteVisible] = useState(true);

  const badge = STAGE_STATUS_BADGES[status];
  const title = stage.title || `Stage ${index + 1}`;
  const week = stage.week || stage.weeks || (index + 1);
  const description = stage.description || '';

  const submitNote = async () => {
    if (!noteDraft.trim()) return;
    await onAdd(index, {
      event_type: 'note',
      note: noteDraft.trim(),
      visible_to_user: noteVisible,
    });
    setNoteDraft('');
  };

  const borderColor =
    status === 'done' ? '#A7F3D0' :
    status === 'active' ? '#FDE68A' : '#E5E7EB';

  return (
    <div
      style={{
        border: `1px solid ${borderColor}`,
        borderRadius: 10,
        background: '#FFFFFF',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '10px 14px',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded((e) => !e)}
      >
        <div
          style={{
            width: 28, height: 28, borderRadius: 999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: status === 'done' ? '#10B981' :
                        status === 'active' ? '#F59E0B' : '#E5E7EB',
            color: status === 'pending' ? '#9CA3AF' : '#FFFFFF',
            fontSize: 12, fontWeight: 700,
          }}
        >
          {status === 'done' ? '✓' : index + 1}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{title}</span>
            <span style={{ fontSize: 11, color: '#9CA3AF' }}>
              · Week {week}
            </span>
            <Badge map={STAGE_STATUS_BADGES} value={status} />
          </div>
          {description && (
            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
              {description}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
          {status !== 'done' && (
            <button
              type="button"
              className="btn-cancel"
              disabled={busy}
              onClick={() =>
                onAdd(index, {
                  event_type: 'started',
                  note: 'Stage started',
                  visible_to_user: true,
                })
              }
              style={{ padding: '4px 10px', fontSize: 11 }}
            >
              Start
            </button>
          )}
          {status !== 'done' && (
            <button
              type="button"
              className="btn-save"
              disabled={busy}
              onClick={() =>
                onAdd(index, {
                  event_type: 'completed',
                  note: 'Stage completed',
                  visible_to_user: true,
                })
              }
              style={{ padding: '4px 10px', fontSize: 11 }}
            >
              Complete
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '0 14px 12px' }}>
          {events.length === 0 ? (
            <div style={{ fontSize: 12, color: '#9CA3AF', padding: '6px 0' }}>
              No events logged for this stage yet.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {events.map((ev) => (
                <StageEventRow
                  key={ev.id}
                  ev={ev}
                  busy={busy}
                  onDelete={onDelete}
                />
              ))}
            </div>
          )}
          <div
            style={{
              marginTop: 10,
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
            }}
          >
            <textarea
              rows={2}
              placeholder="Add a note for this stage…"
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              style={{ flex: 1, minHeight: 36 }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label
                style={{
                  fontSize: 11,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  color: '#6B7280',
                  whiteSpace: 'nowrap',
                }}
              >
                <input
                  type="checkbox"
                  checked={noteVisible}
                  onChange={(e) => setNoteVisible(e.target.checked)}
                />
                Visible to user
              </label>
              <button
                type="button"
                className="btn-save"
                disabled={busy || !noteDraft.trim()}
                onClick={submitNote}
                style={{ padding: '4px 12px', fontSize: 11 }}
              >
                Add Note
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StageEventRow({ ev, busy, onDelete }) {
  const icon =
    ev.event_type === 'started' ? '▶' :
    ev.event_type === 'completed' ? '✓' :
    ev.event_type === 'attachment' ? '📎' : '·';
  const color =
    ev.event_type === 'completed' ? '#166534' :
    ev.event_type === 'started' ? '#92400E' : '#374151';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        padding: '6px 10px',
        background: '#F9FAFB',
        border: '1px solid #F3F4F6',
        borderRadius: 6,
      }}
    >
      <span style={{ color, fontSize: 12, marginTop: 1 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 12,
            color: '#111827',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {ev.note || `(${ev.event_type})`}
        </div>
        <div
          style={{
            fontSize: 10,
            color: '#9CA3AF',
            marginTop: 2,
            display: 'flex',
            gap: 6,
            flexWrap: 'wrap',
          }}
        >
          <span>{new Date(ev.created_at).toLocaleString()}</span>
          {ev.admin_email && <span>· {ev.admin_email}</span>}
          {!ev.visible_to_user && (
            <span style={{ color: '#B45309' }}>· internal</span>
          )}
        </div>
      </div>
      <button
        type="button"
        className="btn-action btn-delete"
        disabled={busy}
        onClick={() => onDelete(ev.id)}
        title="Delete event"
        style={{ width: 22, height: 22 }}
      >
        ×
      </button>
    </div>
  );
}

/* ──────────────────── Action Items editor ──────────────────── */

function ActionItemsEditor({
  stages, actions, busy, newAction, setNewAction,
  onAdd, onPatch, onDelete,
}) {
  const total = actions.filter((a) => a.visible_to_user === 1).length;
  const pending = actions.filter(
    (a) => a.visible_to_user === 1 && a.status !== 'done',
  ).length;

  return (
    <>
      <div
        style={{
          marginTop: 22,
          marginBottom: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.5,
            color: '#9CA3AF',
            textTransform: 'uppercase',
          }}
        >
          Action items ({total}) · {pending} pending
        </span>
        <span style={{ fontSize: 11, color: '#9CA3AF' }}>
          Powers user&apos;s &ldquo;Actions needed&rdquo; counter
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {actions.length === 0 ? (
          <div
            style={{
              padding: 12,
              background: '#F9FAFB',
              border: '1px dashed #E5E7EB',
              borderRadius: 8,
              fontSize: 12,
              color: '#6B7280',
            }}
          >
            No action items yet. Add one below — user sees them as
            pending tasks on the setup-overview card.
          </div>
        ) : (
          actions.map((a) => (
            <ActionRow
              key={a.id}
              action={a}
              stages={stages}
              busy={busy}
              onPatch={(patch) => onPatch(a.id, patch)}
              onDelete={() => onDelete(a.id)}
            />
          ))
        )}

        {/* Add row */}
        <div
          style={{
            marginTop: 6,
            display: 'grid',
            gridTemplateColumns: '1fr 110px 80px',
            gap: 6,
            alignItems: 'center',
          }}
        >
          <input
            type="text"
            placeholder="e.g. Sign Power of Attorney"
            value={newAction.title}
            onChange={(e) =>
              setNewAction((p) => ({ ...p, title: e.target.value }))
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') onAdd();
            }}
          />
          <select
            value={newAction.assigned_to}
            onChange={(e) =>
              setNewAction((p) => ({ ...p, assigned_to: e.target.value }))
            }
          >
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
          <button
            type="button"
            className="btn-save"
            disabled={busy || !newAction.title.trim()}
            onClick={onAdd}
            style={{ padding: '6px 10px', fontSize: 11 }}
          >
            + Add
          </button>
        </div>
      </div>
    </>
  );
}

function ActionRow({ action, stages, busy, onPatch, onDelete }) {
  const done = action.status === 'done';
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '24px 1fr 110px 120px 30px',
        gap: 8,
        alignItems: 'center',
        padding: '8px 10px',
        background: done ? '#ECFDF5' : '#FFFFFF',
        border: `1px solid ${done ? '#A7F3D0' : '#E5E7EB'}`,
        borderRadius: 8,
      }}
    >
      <input
        type="checkbox"
        checked={done}
        disabled={busy}
        onChange={(e) =>
          onPatch({ status: e.target.checked ? 'done' : 'todo' })
        }
      />
      <input
        type="text"
        value={action.title}
        disabled={busy}
        onChange={(e) => onPatch({ title: e.target.value })}
        onBlur={(e) => {
          if (e.target.value.trim() !== action.title) {
            onPatch({ title: e.target.value });
          }
        }}
        style={{
          textDecoration: done ? 'line-through' : 'none',
          color: done ? '#6B7280' : '#111827',
          background: 'transparent',
        }}
      />
      <select
        value={action.assigned_to}
        disabled={busy}
        onChange={(e) => onPatch({ assigned_to: e.target.value })}
        style={{ fontSize: 11 }}
      >
        <option value="user">User</option>
        <option value="admin">Admin</option>
      </select>
      <select
        value={action.stage_index ?? ''}
        disabled={busy}
        onChange={(e) =>
          onPatch({
            stage_index: e.target.value === ''
              ? null
              : Number(e.target.value),
          })
        }
        style={{ fontSize: 11 }}
      >
        <option value="">(no stage)</option>
        {stages.map((s, i) => (
          <option key={i} value={i}>
            Week {s.week ?? i + 1}: {s.title?.slice(0, 22)}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="btn-action btn-delete"
        disabled={busy}
        onClick={onDelete}
        title="Delete"
        style={{ width: 24, height: 22 }}
      >
        ×
      </button>
    </div>
  );
}

function parseJson(v, fallback) {
  if (Array.isArray(v) || (v && typeof v === 'object')) return v;
  if (typeof v === 'string') {
    try { return JSON.parse(v); } catch { return fallback; }
  }
  return fallback;
}

/* ──────────── Incorporation completion → entity materialisation ──────────── */
// Mirrors entities.legal_type. The admin picks the real legal type; we only
// pre-select a best guess from the proposal's jurisdiction + structure.
const LEGAL_TYPE_OPTIONS = [
  { value: 'delaware_c_corp', label: 'Delaware C-Corp' },
  { value: 'delaware_llc', label: 'Delaware LLC' },
  { value: 'wyoming_llc', label: 'Wyoming LLC' },
  { value: 'wyoming_c_corp', label: 'Wyoming C-Corp' },
  { value: 'india_pvt_ltd', label: 'India Pvt Ltd' },
  { value: 'india_llp', label: 'India LLP' },
  { value: 'uae_freezone', label: 'UAE Free Zone' },
  { value: 'uae_mainland', label: 'UAE Mainland' },
  { value: 'thailand_co_ltd', label: 'Thailand Co., Ltd.' },
  { value: 'singapore_pte_ltd', label: 'Singapore Pte Ltd' },
  { value: 'uk_ltd', label: 'UK Ltd' },
  { value: 'other', label: 'Other' },
];
const LEGAL_TYPE_LABEL = Object.fromEntries(
  LEGAL_TYPE_OPTIONS.map((o) => [o.value, o.label]),
);

// The compliance engine keys state-scoped filings (e.g. Delaware franchise tax)
// on a 2-letter code, so pre-fill it from the jurisdiction the founder booked.
const STATE_PREFILL = { delaware: 'DE', wyoming: 'WY' };

function guessLegalType(slug = '', structure = '') {
  const s = `${slug || ''} ${structure || ''}`.toLowerCase();
  const isLLC = /llc|limited liability/.test(s);
  if (/delaware|\bde\b/.test(s)) return isLLC ? 'delaware_llc' : 'delaware_c_corp';
  if (/wyoming|\bwy\b/.test(s)) return isLLC ? 'wyoming_llc' : 'wyoming_c_corp';
  if (/india/.test(s)) return /llp/.test(s) ? 'india_llp' : 'india_pvt_ltd';
  if (/mainland/.test(s)) return 'uae_mainland';
  if (/uae|dubai|emirates|free ?zone/.test(s)) return 'uae_freezone';
  if (/singapore/.test(s)) return 'singapore_pte_ltd';
  if (/thailand/.test(s)) return 'thailand_co_ltd';
  if (/\buk\b|united kingdom|britain|england/.test(s)) return 'uk_ltd';
  return 'other';
}

function EntityCompletionPanel({ item, onCompleted }) {
  const entity = item?.entity || null;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState(null);
  const [justCreated, setJustCreated] = useState(null);

  // The founder's applied name choice(s). Self-serve incorporations in
  // jurisdictions that need three names store them comma-separated; the admin
  // finalises ONE. Keep the full list as an ops-only reference/log.
  const appliedNames = String(item?.entity_legal_name || item?.title || '')
    .split(',').map((s) => s.trim()).filter(Boolean);
  const firstChoice = appliedNames[0] || '';
  const hasNameChoices = appliedNames.length > 1;

  const startForm = () => {
    setForm({
      // Default to the founder's FIRST choice — ops overrides if it wasn't
      // available at the registry. Never prefill all three into one name field.
      name: firstChoice || item?.title || '',
      legal_type: guessLegalType(
        item?.jurisdiction_slug,
        `${item?.entity_structure || ''} ${firstChoice || item?.title || ''}`,
      ),
      state_or_region: STATE_PREFILL[(item?.jurisdiction_slug || '').toLowerCase()] || '',
      incorporation_date: '',
      ein: '',
      foreign_owned: false,
      has_employees: false,
      parent_entity_name: '',
      notes: '',
    });
    setErr(null);
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) { setErr('Company name is required'); return; }
    if (!form.incorporation_date) { setErr('Incorporation date is required'); return; }
    setBusy(true);
    setErr(null);
    try {
      const res = await naytiveAPI.completeIncorporation(item.id, {
        name: form.name.trim(),
        legal_type: form.legal_type,
        state_or_region: form.state_or_region.trim() || undefined,
        incorporation_date: form.incorporation_date,
        ein: form.ein.trim() || undefined,
        foreign_owned: form.foreign_owned,
        has_employees: form.has_employees,
        parent_entity_name: form.parent_entity_name.trim() || undefined,
        notes: form.notes.trim() || undefined,
      });
      setJustCreated({
        filings: res.data?.filings_created ?? null,
        regenError: res.data?.regenerate?.ok === false
          ? (res.data.regenerate.reason || 'unknown') : null,
      });
      setOpen(false);
      await onCompleted();
    } catch (e) {
      setErr(e.response?.data?.message || 'Failed to complete incorporation');
    } finally {
      setBusy(false);
    }
  };

  const wrap = {
    border: '1px solid #E5E7EB',
    borderRadius: 10,
    padding: '14px 16px',
    marginBottom: 18,
    background: '#FFFFFF',
  };

  // Already materialised → company summary (no form).
  if (entity) {
    const Cell = ({ label, value }) => (
      <div>
        <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.4, color: '#059669', fontWeight: 700 }}>{label}</div>
        <div style={{ fontSize: 13, color: '#064E3B', marginTop: 2 }}>{value}</div>
      </div>
    );
    return (
      <div style={{ ...wrap, background: '#F0FDF4', borderColor: '#BBF7D0' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: '#047857', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
          ✓ Incorporation complete
        </div>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#065F46', marginBottom: hasNameChoices ? 4 : 10 }}>{entity.name}</div>
        {hasNameChoices && (
          <div style={{ fontSize: 11.5, color: '#6B7280', marginBottom: 10 }}>
            Applied name choices: {appliedNames.join('  ·  ')}
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <Cell label="Legal type" value={LEGAL_TYPE_LABEL[entity.legal_type] || entity.legal_type} />
          <Cell label="Incorporated" value={fmtDate(entity.incorporation_date)} />
          <Cell label="EIN" value={entity.ein || 'Pending'} />
          <Cell label="Country" value={entity.registered_country_name || entity.registered_country_code || '—'} />
          <Cell label="Entity" value={`#${entity.id}`} />
          <Cell label="Workspace" value={`#${entity.workspace_id}`} />
        </div>
        {justCreated ? (
          justCreated.regenError ? (
            <div style={{ fontSize: 12, color: '#B45309', marginTop: 10 }}>
              Entity created, but the compliance calendar could not be generated
              ({justCreated.regenError}). Use “Regenerate” once the user panel is reachable.
            </div>
          ) : (
            <div style={{ fontSize: 12, color: '#047857', marginTop: 10 }}>
              Compliance calendar generated{typeof justCreated.filings === 'number'
                ? ` — ${justCreated.filings} filing${justCreated.filings === 1 ? '' : 's'} created`
                : ''}.
            </div>
          )
        ) : null}
        <EntityFilings entity={entity} />
      </div>
    );
  }

  // Not yet complete → CTA + inline form.
  return (
    <div style={wrap}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#111827' }}>Incorporation status</div>
          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2, maxWidth: 520 }}>
            Not yet materialised. Complete this once the company legally exists
            (Certificate of Formation filed) to create the entity and unlock its
            compliance calendar. EIN can be added later.
          </div>
        </div>
        {!open && (
          <button type="button" className="btn-primary" onClick={startForm} style={{ whiteSpace: 'nowrap' }}>
            Mark incorporation complete
          </button>
        )}
      </div>

      {open && form && (
        <div style={{ marginTop: 14 }}>
          {hasNameChoices && (
            <div style={{ fontSize: 12, color: '#6B7280', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: '8px 10px', marginBottom: 12 }}>
              <span style={{ fontWeight: 600, color: '#374151' }}>Founder applied with {appliedNames.length} name choices:</span>{' '}
              {appliedNames.map((n, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, name: n }))}
                  style={{ margin: '0 4px 0 0', padding: '2px 8px', fontSize: 12, border: '1px solid #D1D5DB', borderRadius: 999, background: form.name === n ? '#DBEAFE' : '#FFFFFF', color: '#111827', cursor: 'pointer' }}
                >
                  {i + 1}. {n}
                </button>
              ))}
              <div style={{ marginTop: 4, color: '#9CA3AF' }}>Set the ONE name the registry actually approved. The choices above are kept on record.</div>
            </div>
          )}
          <div className="form-row">
            <div className="form-group">
              <label>Company name *</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Legal type *</label>
              <select value={form.legal_type} onChange={(e) => setForm({ ...form, legal_type: e.target.value })}>
                {LEGAL_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Incorporation date *</label>
              <input type="date" value={form.incorporation_date} onChange={(e) => setForm({ ...form, incorporation_date: e.target.value })} />
            </div>
            <div className="form-group">
              <label>State / region</label>
              <input value={form.state_or_region} placeholder="e.g. Delaware" onChange={(e) => setForm({ ...form, state_or_region: e.target.value })} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>EIN <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(optional — often issued later)</span></label>
              <input value={form.ein} onChange={(e) => setForm({ ...form, ein: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Parent entity name <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(optional)</span></label>
              <input value={form.parent_entity_name} placeholder="e.g. Mate Finance LLC-FZ" onChange={(e) => setForm({ ...form, parent_entity_name: e.target.value })} />
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '4px 0 12px' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontWeight: 400 }}>
              <input type="checkbox" checked={form.foreign_owned} onChange={(e) => setForm({ ...form, foreign_owned: e.target.checked })} style={{ marginTop: 2 }} />
              <span>
                <span style={{ fontWeight: 600 }}>Foreign-owned (≥25% non-US ownership)</span>
                <span style={{ display: 'block', fontSize: 12, color: '#6B7280' }}>
                  Drives IRS Form 5472 for US LLCs / C-Corps. Leave unchecked if US-owned.
                </span>
              </span>
            </label>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontWeight: 400 }}>
              <input type="checkbox" checked={form.has_employees} onChange={(e) => setForm({ ...form, has_employees: e.target.checked })} style={{ marginTop: 2 }} />
              <span>
                <span style={{ fontWeight: 600 }}>Has employees</span>
                <span style={{ display: 'block', fontSize: 12, color: '#6B7280' }}>
                  Drives payroll returns (e.g. US Form 941, India TDS).
                </span>
              </span>
            </label>
          </div>
          <div className="form-group full-width">
            <label>Notes <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(optional)</span></label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          {err && <div style={{ color: '#DC2626', fontSize: 12, marginBottom: 8 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn-primary" disabled={busy} onClick={submit}>
              {busy ? 'Creating…' : 'Create entity & unlock compliance'}
            </button>
            <button type="button" className="btn-cancel" disabled={busy} onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ──────────── Per-entity compliance filing calendar (admin cockpit) ──────────── */
const SEVERITY_COLOR = {
  critical: '#DC2626', high: '#EA580C', medium: '#CA8A04', low: '#6B7280',
};
const FILING_STATUS_STYLE = {
  upcoming: { bg: '#EFF6FF', fg: '#1D4ED8', label: 'Upcoming' },
  urgent: { bg: '#FFF7ED', fg: '#C2410C', label: 'Urgent' },
  in_progress: { bg: '#F5F3FF', fg: '#6D28D9', label: 'In progress' },
  filed: { bg: '#ECFDF5', fg: '#047857', label: 'Filed' },
  overdue: { bg: '#FEF2F2', fg: '#B91C1C', label: 'Overdue' },
  waived: { bg: '#F3F4F6', fg: '#4B5563', label: 'Waived' },
};
// Statuses an admin can set directly (overdue/urgent are engine-computed).
const SETTABLE_STATUSES = ['upcoming', 'in_progress', 'filed', 'waived'];

function EntityFilings({ entity }) {
  const entityId = entity?.id;
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ template_id: '', due_date: '', period_label: '' });

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const r = await naytiveAPI.listEntityFilings(entityId);
      setFilings(r.data?.items || []);
      setErr(null);
    } catch (e) {
      setErr(e.response?.data?.message || 'Failed to load filings');
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  useEffect(() => { load(); }, [load]);

  const regenerate = async () => {
    setBusy(true);
    setErr(null);
    try {
      await naytiveAPI.regenerateEntityFilings(entityId);
      await load();
    } catch (e) {
      setErr(e.response?.data?.message || 'Regeneration failed (is the user panel running?)');
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (id, status) => {
    setBusy(true);
    try {
      await naytiveAPI.updateComplianceFiling(id, { status });
      await load();
    } catch (e) {
      setErr(e.response?.data?.message || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const openAdd = async () => {
    setAddOpen(true);
    if (templates.length === 0) {
      try {
        const r = await naytiveAPI.listFilingTemplates();
        setTemplates(r.data?.items || []);
      } catch { /* non-fatal */ }
    }
  };

  const submitAdd = async () => {
    if (!addForm.template_id || !addForm.due_date) {
      setErr('Pick a template and a due date');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await naytiveAPI.addEntityFiling(entityId, {
        template_id: Number(addForm.template_id),
        due_date: addForm.due_date,
        period_label: addForm.period_label.trim() || undefined,
      });
      setAddForm({ template_id: '', due_date: '', period_label: '' });
      setAddOpen(false);
      await load();
    } catch (e) {
      setErr(e.response?.data?.message || 'Failed to add filing');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 14, borderTop: '1px solid #BBF7D0', paddingTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, color: '#065F46', textTransform: 'uppercase' }}>
          Compliance calendar {filings.length ? `(${filings.length})` : ''}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" className="btn-cancel" disabled={busy} onClick={openAdd} style={{ padding: '4px 10px', fontSize: 12 }}>
            + Add filing
          </button>
          <button type="button" className="btn-cancel" disabled={busy} onClick={regenerate} style={{ padding: '4px 10px', fontSize: 12 }}>
            {busy ? '…' : 'Regenerate'}
          </button>
        </div>
      </div>

      {err && <div style={{ color: '#B91C1C', fontSize: 12, marginBottom: 8 }}>{err}</div>}

      {addOpen && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px 120px auto', gap: 8, alignItems: 'center', background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 8, padding: 10, marginBottom: 10 }}>
          <select value={addForm.template_id} onChange={(e) => setAddForm({ ...addForm, template_id: e.target.value })}>
            <option value="">Select filing template…</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>{t.country_code ? `${t.country_code} · ` : ''}{t.name} ({t.code})</option>
            ))}
          </select>
          <input type="date" value={addForm.due_date} onChange={(e) => setAddForm({ ...addForm, due_date: e.target.value })} />
          <input type="text" placeholder="Period (FY2027)" value={addForm.period_label} onChange={(e) => setAddForm({ ...addForm, period_label: e.target.value })} />
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" className="btn-primary" disabled={busy} onClick={submitAdd} style={{ padding: '4px 10px', fontSize: 12 }}>Add</button>
            <button type="button" className="btn-cancel" disabled={busy} onClick={() => setAddOpen(false)} style={{ padding: '4px 10px', fontSize: 12 }}>×</button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ fontSize: 12, color: '#6B7280' }}>Loading…</div>
      ) : filings.length === 0 ? (
        <div style={{ fontSize: 12, color: '#6B7280' }}>
          No filings yet. Click “Regenerate” to compute this entity’s calendar from its jurisdiction.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {filings.map((f) => {
            const st = FILING_STATUS_STYLE[f.status] || FILING_STATUS_STYLE.upcoming;
            return (
              <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 8, padding: '8px 10px' }}>
                <span title={f.severity} style={{ width: 8, height: 8, borderRadius: 4, background: SEVERITY_COLOR[f.severity] || '#9CA3AF', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                    {f.template_name} <span style={{ color: '#9CA3AF', fontWeight: 400 }}>({f.template_code})</span>
                  </div>
                  <div style={{ fontSize: 11.5, color: '#6B7280' }}>
                    Due {fmtDate(f.due_date)}{f.period_label ? ` · ${f.period_label}` : ''}{f.filed_at ? ` · filed ${fmtDate(f.filed_at)}` : ''}
                  </div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, color: st.fg, background: st.bg, padding: '2px 8px', borderRadius: 999 }}>{st.label}</span>
                <select value={SETTABLE_STATUSES.includes(f.status) ? f.status : ''} disabled={busy} onChange={(e) => e.target.value && setStatus(f.id, e.target.value)} style={{ fontSize: 12, padding: '3px 6px' }}>
                  {!SETTABLE_STATUSES.includes(f.status) && <option value="">{st.label}…</option>}
                  <option value="upcoming">Upcoming</option>
                  <option value="in_progress">In progress</option>
                  <option value="filed">Mark filed</option>
                  <option value="waived">Waive</option>
                </select>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default NaytiveProposals;



